import { NavLink } from 'react-router-dom'
import { Home, Map, Activity, Link2, Search, Wrench } from 'lucide-react'
import { contentIndexFor } from '@/lib/content'
import { toolsIndexFor } from '@/lib/tools'
import { useI18n } from '@/i18n'
import { CATEGORIES } from '@/lib/categories'
import { SidebarPromo } from '@/components/SidebarPromo'

type Props = {
  onSearchClick: () => void
  onNavigate?: () => void
}

export function Sidebar({ onSearchClick, onNavigate }: Props) {
  const { t, lang, langPath } = useI18n()
  const contentIndex = contentIndexFor(lang)
  const toolsIndex = toolsIndexFor(lang)
  const cats = contentIndex.categories.filter((c) => c.count > 0 || c.id === 'uncategorized')

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <h1>{t.appTitle}</h1>
        <p>{t.appSubtitle}</p>
      </div>
      <div style={{ padding: '12px' }}>
        <button className="btn" style={{ width: '100%' }} onClick={onSearchClick}>
          <Search size={16} /> {t.nav.search} <span className="kbd">Ctrl+K</span>
        </button>
      </div>
      <nav className="sidebar-nav">
        <NavLink to={langPath('/')} end className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} onClick={onNavigate}>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Home size={16} /> {t.nav.home}</span>
        </NavLink>
        <NavLink to={langPath('/explore')} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} onClick={onNavigate}>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Map size={16} /> {t.nav.explore}</span>
        </NavLink>
        <NavLink to={langPath('/status')} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} onClick={onNavigate}>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Activity size={16} /> {t.nav.status}</span>
        </NavLink>
        <NavLink to={langPath('/attack-chains')} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} onClick={onNavigate}>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Link2 size={16} /> {t.nav.chains}</span>
          <span className="badge-count">{contentIndex.chains.length}</span>
        </NavLink>

        <NavLink to={langPath('/tools')} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} onClick={onNavigate}>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Wrench size={16} /> {t.nav.tools}</span>
          <span className="badge-count">{toolsIndex.counts.tools}</span>
        </NavLink>

        <details className="cat-group" open>
          <summary>{t.nav.categories}</summary>
          {CATEGORIES.map((meta) => {
            const c = cats.find((x) => x.id === meta.id) || { ...meta, count: 0 }
            if (c.count <= 0 && meta.id !== 'uncategorized') return null
            return (
              <NavLink
                key={meta.id}
                to={langPath(`/explore?category=${meta.id}`)}
                className={({ isActive }) => `cat-link${isActive ? ' active' : ''}`}
                onClick={onNavigate}
              >
                <span>{meta.order < 10 ? `0${meta.order}` : meta.order}. {meta.label}</span>
                <span className="badge-count">{c.count}</span>
              </NavLink>
            )
          })}
        </details>
      </nav>
      <SidebarPromo />
    </aside>
  )
}
